import { Product } from './orders';
import { TaskSummary } from './task';
import { UserReferenceModel } from './user/base';

export enum HotelIntegrationProviders {
  Passkey = 'passkey',
  Meetingmax = 'meetingmax',
  Test = 'test',
}

export const HotelIntegrationProviderNames = {
  [HotelIntegrationProviders.Passkey]: 'Passkey',
  [HotelIntegrationProviders.Meetingmax]: 'MeetingMax',
  [HotelIntegrationProviders.Test]: 'Test',
};

interface HotelIntegrationCredentialBase {
  type: HotelIntegrationProviders;
  environment?: 'production' | 'sandbox';
}

export interface ReleaseEntryDisqualification {
  disqualifiedBy?: number;
  isSystemAction?: boolean;
  disqualifiedAt: Date;
  reason: string;
}

export interface PasskeyAttendeeType {
  id: number;
  name: string;
  codes: string[];
}

export interface PasskeyCredential extends HotelIntegrationCredentialBase {
  type: HotelIntegrationProviders.Passkey;

  /// The unique identifier for the event in Passkey
  eventId: string;

  /**
   * The temporary access code for the event in Passkey.
   *
   * This is used to initially associate the event with ConCat's
   * integration provider account. This should be cleared after
   * the event is associated.
   *
   * See @{link https://developers.cvent.com/documentation#tag/Housing/operation/createConnection}
   * for more information.
   */
  accessCode?: string;

  /// The attendeeTypes available for the event in Passkey
  attendeeTypes?: PasskeyAttendeeType[];
}

export interface MeetingmaxCredential extends HotelIntegrationCredentialBase {
  type: HotelIntegrationProviders.Meetingmax;

  /// The unique identifier for the event in Meetingmax
  eventId: number;

  /// The API secret for the event in Meetingmax
  key: string;

  /// The integration system identifier for ConCat in Meetingmax
  systemIdentifier: string;
}

export enum HotelIntegrationStatus {
  First_Time_Sync = 'first_time_sync',
  Synchronized = 'synchronized',
  Syncing = 'syncing',
  Sync_Error = 'sync_error',
  Disconnected = 'disconnected',
}

export enum HotelReservationStatus {
  /// The reservation link is being generated in the integration provider.
  Creating = 'creating',
  /// The attendee has not yet reserved a room using their booking URL.
  Pending = 'pending',
  /// The attendee has reserved a room using their booking URL, but it has not yet been confirmed.
  New = 'new',
  /// The attendee has reserved a room using their booking URL, and the reservation is confirmed.
  Confirmed = 'confirmed',
  /// The reservation has been cancelled via the integration provider.
  Cancelled = 'cancelled',
  /// The reservation was cancelled by the event through ConCat, or otherwise invalidated by the event.
  CancelledByEvent = 'cancelled_by_event',
  /// The reservation is confirmed but is waitlisted, meaning the hotel has no rooms available at the moment.
  Waitlisted = 'waitlisted',
  /// The attendee reserved a room but did not show up at check-in, and the reservation was cancelled by the hotel.
  NoShow = 'no_show',
  /// The reservation link was expired before being used.
  Expired = 'expired',
}

enum CancellationTermRefundType {
  /// The refund is for the full amount of the reservation.
  Full = 'full',
  /// The refund is for a partial amount of the reservation.
  Partial = 'partial',
  /// No refund is provided for the reservation.
  NoRefund = 'no_refund',
  /// Catch-all for if there are any other refund types not covered by the above.
  Other = 'other',
}

interface HotelReservationCancellationTerm {
  name: string;
  refundType: CancellationTermRefundType;
  cancelBy: Date;
  additionalInfo?: string;
}

export interface HotelReservationCancellationTerms {
  termsFullText: string;
  terms: HotelReservationCancellationTerm[];
}

export interface HotelReservationGuarantee {
  type: 'card' | 'none' | 'other';
  name?: string;
  reference?: string;
  additionalMeta?: object;
}

export enum ReleaseGroupVisibility {
  Hidden = 'hidden',
  AlwaysVisible = 'always_visible',
  EligibleOnly = 'eligible_only',
}

export enum ReleaseGroupType {
  FirstComeFirstServe = 'fcfs',
  RandomDraw = 'random',
}

export enum ReleaseGroupStatus {
  /// The release group is in draft mode and not visible to attendees.
  Draft = 'draft',

  /*
   * The release group is ready for attendees to view and enter, or is otherwise
   * not in a state that requires action.
   */
  Ready = 'ready',

  /// The release group has finished and is archived.
  Finished = 'finished',
}

export enum ReleaseGroupStage {
  // Transient statuses
  PreEntry = 'pre_entry',
  EntryPeriod = 'entry_period',
  PreReleaseReady = 'pre_release_ready',
  ReleasePeriod = 'release_period',
  Finished = 'finished',
}

export enum ReleaseRequireRegistration {
  Optional = 'optional',
  Required = 'required',
  RequirePaid = 'require_paid',
}

export enum ReleaseForceEntry {
  None = 'none',
  Force = 'force',
  ForceByRoleIds = 'force_by_role_ids',
  ForceByProductIds = 'force_by_product_ids',
  ForceByRoleOrProduct = 'force_by_role_or_product',
  ForceByRoleAndProduct = 'force_by_role_and_product',
}

export interface HotelPublicInformation {
  releaseGroups: PublicHotelReleaseGroup[];
  reservations: HotelReservation[];
  allowGroupBookings: boolean;
}

// Base class that contains all information about a release group that should be visible to both admins and public users
export interface HotelReleaseGroup {
  id: string;
  conventionId: number;
  name: string;
  displayName?: string;
  description?: string;
  type: ReleaseGroupType;
  status: ReleaseGroupStatus;
  visibility: ReleaseGroupVisibility;
  entryStartAt: Date | null;
  entryEndAt: Date | null;
  releaseStartAt: Date;
  releaseEndAt: Date;
  validityPeriod?: number;
  showRoomAvailability: boolean;
  requireRegistration: ReleaseRequireRegistration;
  forceEntry: ReleaseForceEntry;
  integrationId: string;

  // Transient fields
  requiredRoles: { id: number; name: string }[];
  requiredProducts: Pick<Product, 'displayName' | 'id' | 'name'>[];

  // TODO: This is never set
  rooms?: HotelRoom[];
}

// A release item when being displayed to the public
export interface PublicHotelReleaseGroup extends HotelReleaseGroup {
  // Specific breakdown of eligibility requirements and which ones the user meets as well as a gross calculation
  eligibility: HotelReleaseEligibility;
}

// A release item when it is being edited by an admin
export interface ExtendedHotelReleaseGroup extends HotelReleaseGroup {
  showRoomAvailability: boolean;
  meta: Record<string, string>;
  createdAt: Date;
  updatedAt: Date;
  deletedAt: Date | null;

  createLinksTask?: TaskSummary;
  sendEmailsTask?: TaskSummary;
}

// Describes the eligibility of a user to enter a particular release group
export interface HotelReleaseEligibility {
  // Relevant groupId
  releaseGroupId: string;
  // User the eligibility is being calculated against
  userId: number;
  // Whether or not the relevant product requirements have been meet
  meetsProduct: boolean;
  // Whether or not the relevant role requirements have been meet
  meetsRole: boolean;
  // Whether or not the relevant registration requirements have been meet
  meetsRegistration: boolean;
  // Whether or not the user is disqualified by having won a raffle
  meetsRequiredNotWon: boolean;
  // Gross calculation of eligibility
  isEligible: boolean;
  // Should the release be visible?
  isVisible: boolean;
}

export interface HotelReservationGuest {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  specialRequests?: string;
  userId?: number;
  addressLine1?: string;
  addressLine2?: string;
  addressCity?: string;
  addressState?: string;
  addressCountry?: string;
  addressZipcode?: string;
  isPrimaryGuest?: boolean;
}

export interface HotelRoomPolicies {
  /// Applies to all group codes (if the policy is the same for all groups)
  globalPolicies?: HotelAgreements;

  /// Policies that apply to specific group codes, where the key is the group code and the value is an array of policies
  groupCodePolicies?: Record<string, HotelAgreements>;
}

export interface HotelRoomExtendedAttributes {
  rating?: string;
  ratingNum?: number;
  amenities?: string[];
  policies?: HotelRoomPolicies;
  images?: string[];
  smoking?: boolean;
  ignoreOccupancy?: boolean;
}

export interface HotelRoom {
  id: string;
  conventionId: number;
  name: string;
  description?: string;
  maxOccupancy: number;
  extendedAttributes?: HotelRoomExtendedAttributes;
}

interface HotelRoomAvailability {
  id: string;
  conventionId: number;
  hotelRoomId: string;
  count: number;
  rate: number;
  availableOn: string;
}

export interface Hotel {
  id: string;
  conventionId: number;
  name: string;
  description?: string;
  website?: string;
  phone?: string;
  isVisible?: boolean;
  extendedAttributes?: Record<string, unknown>;
  addressLine1: string;
  addressLine2?: string;
  addressCity: string;
  addressCountry: string;
  addressState: string;
  addressZipcode: string;
}

export interface FullHotel {
  id: string;
  name: string;
  description?: string;
  website?: string | null;
  phone?: string | null;
  externalId?: string;
  addressLine1: string;
  addressLine2?: string;
  addressCity: string;
  addressCountry: string;
  addressState: string;
  addressZipcode: string;
  isVisible: boolean;
  conventionId: number;
  integrationId: string;
  rooms: (HotelRoom & { availability: HotelRoomAvailability[] })[];
  extendedAttributes?: Record<string, unknown>;
}

type HotelAgreementKeys =
  | 'reservationAgreement'
  | 'roomReservationAgreement'
  | 'cancellationAgreement';

export type HotelAgreements = Record<HotelAgreementKeys, string>;

export interface HotelAdditionalFee {
  key: string;
  date?: string;
  amount: number;
}

export interface HotelReservationExtAttrs {
  rate?: {
    total?: number;
    breakdown?: Record<string, number>;
    additionalFees?: HotelAdditionalFee[];
  };
  hotelConfirmationNumber?: string;
  specialRequests?: string;
  agreements?: HotelAgreements;
  organizerExtUrl?: string;
  isReservationRequest?: boolean;
}

export interface HotelReservation {
  id: string;
  conventionId: number;
  integrationId: string;
  status: HotelReservationStatus;
  deletedAt?: Date;

  checkIn?: string;
  checkOut?: string;
  extReservationId?: string;
  primaryGuestId?: string;
  userId?: number;
  specialRequests?: string;
  cancellationTerms?: HotelReservationCancellationTerms;
  guarantee?: HotelReservationGuarantee;
  hasUrl: boolean;
  bookingUrl?: string;
  extendedAttributes?: HotelReservationExtAttrs;
  room?: HotelRoom;
  hotel?: Hotel;
  guests?: HotelReservationGuest[];

  managedBy?: HotelIntegrationProviders;

  allowGroupBookings?: boolean;

  updatedAt?: Date;
}

export interface HotelIntegration {
  id: string;
  name: string;
  type: HotelIntegrationProviders;
  credentials?: object;
  webhookSecret?: string;
  status?: HotelIntegrationStatus;
  enabled: boolean;
  lastSyncAt: Date | null;
  lastManualSyncAt: Date | null;
  conventionId: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface HotelReleaseGroupUpdateAttributes {
  name: string;
  displayName?: string;
  description?: string;
  entryStartAt: Date | null;
  entryEndAt: Date | null;
  releaseStartAt: Date;
  releaseEndAt: Date;
  status?: ReleaseGroupStatus;
  requireRegistration: ReleaseRequireRegistration;
  requireNotWon?: boolean;
  visibility?: ReleaseGroupVisibility;
  showRoomAvailability: boolean;
  meta?: Record<string, string>;
  forceEntry?: ReleaseForceEntry;
  requiredProductIds: number[];
  requiredRoleIds: number[];
}

export interface HotelReleaseGroupCreateAttributes extends HotelReleaseGroupUpdateAttributes {
  type: ReleaseGroupType;
  validityPeriod?: number;
  showRoomAvailability: boolean;
  integrationId: string;
}

export interface HotelReleaseGroupEntryList {
  name: string;
  displayName: string | null;
  entries: HotelReleaseGroupEntries[];
}

export interface HotelReleaseGroupEntries {
  userId: number;
  releaseGroupId: string;
  isSelected?: boolean;
  isNotified?: boolean;
  id: string;
  conventionId: number;
  createdAt: Date;
  updatedAt: Date;
  deletedAt: Date | null;
  reservation?: HotelReservation;
  user: UserReferenceModel;
  userEmailVerified: boolean;
}

export interface HotelStats {
  totalHotels?: number;
  totalRooms?: number;
  totalAvailableRooms?: number;
  totalReservations?: number;
  totalReleaseGroups?: number;
  totalReleaseGroupEntries?: number;
}

export interface ReleaseGroupCreateAttributes {
  name: string;
  displayName?: string;
  description?: string;
  entryStartAt?: Date | null;
  entryEndAt?: Date | null;
  type?: ReleaseGroupType;
  status?: ReleaseGroupStatus;
  releaseStartAt: Date;
  releaseEndAt: Date;
  validityPeriod?: number;
  requireRegistration: ReleaseRequireRegistration;
  requireBothRolesAndProducts?: boolean;
  requireNotWon?: boolean;
  showRoomAvailability: boolean;
  forceEntry?: ReleaseForceEntry;
  visibility?: ReleaseGroupVisibility;
  integrationId: string;
  createLinksTask?: string | null;
  sendEmailsTask?: string | null;
  meta?: Record<string, string>;
  requiredProductIds?: number[];
  requiredRoleIds?: number[];
  maxWinners?: number | null;
}

export interface ReleaseGroupEntryExtended {
  id: string;
  releaseGroup: PublicHotelReleaseGroup;
  isNotified: boolean;
  isSelected: boolean;
  reservationId: string | null;
  disqualification: ReleaseEntryDisqualification | null;
  eligibility: HotelReleaseEligibility;
  reservationTask?: TaskSummary;
}

export interface UserHotelSummary {
  user: UserReferenceModel;
  entries: ReleaseGroupEntryExtended[];
  reservations: HotelReservation[];
}

export function inReleasePeriod(group: HotelReleaseGroup, time: Date): boolean {
  return time >= group.releaseStartAt && time <= group.releaseEndAt;
}

export function getReleasePeriod(
  group: Pick<HotelReleaseGroup, 'entryStartAt' | 'entryEndAt' | 'releaseStartAt' | 'releaseEndAt'>,
  now: Date,
): ReleaseGroupStage {
  if (group.entryStartAt && group.entryStartAt > now) {
    return ReleaseGroupStage.PreEntry;
  }

  if (group.entryEndAt && group.entryEndAt > now) {
    return ReleaseGroupStage.EntryPeriod;
  }

  if (group.releaseStartAt > now) {
    return ReleaseGroupStage.PreReleaseReady;
  }

  if (group.releaseEndAt > now) {
    return ReleaseGroupStage.ReleasePeriod;
  }

  return ReleaseGroupStage.Finished;
}

// TODO: make and use translation keys
export function stageToText(stage: ReleaseGroupStage): string {
  switch (stage) {
    case ReleaseGroupStage.PreEntry: {
      return 'Pre Entry';
    }

    case ReleaseGroupStage.EntryPeriod: {
      return 'Entry Period';
    }

    case ReleaseGroupStage.Finished: {
      return 'Finished';
    }

    case ReleaseGroupStage.PreReleaseReady: {
      return 'Prerelease Ready';
    }

    case ReleaseGroupStage.ReleasePeriod: {
      return 'Release Period';
    }

    default: {
      return 'Unknown';
    }
  }
}

// TODO: make and use translation keys
export function groupTypeToText(type: ReleaseGroupType): string {
  switch (type) {
    case ReleaseGroupType.FirstComeFirstServe: {
      return 'First Come First Serve';
    }

    case ReleaseGroupType.RandomDraw: {
      return 'Random Draw';
    }

    default: {
      return 'Unknown';
    }
  }
}
